import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  Typography,
  List,
  ListItem,
  ListItemContent,
  Chip,
  IconButton,
  Badge,
  Sheet,
  Button,
  Stack,
  Divider,
  CircularProgress,
  Modal,
  ModalDialog,
  DialogTitle,
  DialogContent
} from '@mui/joy';
import { FaBell, FaEye } from 'react-icons/fa';
import { IconWrapper } from './IconWrapper';
import { directSupabase } from '../lib/supabaseClient';
import { format } from 'date-fns';

interface Notification {
  id: string;
  created_at: string;
  type: string;
  message: string;
  is_read: boolean;
  metadata?: any;
}

const NotificationsDashboard: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);

  // Load initial notifications
  useEffect(() => {
    loadNotifications();
  }, []);

  // Subscribe to real-time notifications
  useEffect(() => {
    const channel = directSupabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications'
        },
        (payload: any) => {
          console.log('New notification received:', payload.new);
          setNotifications(prev => [payload.new as Notification, ...prev]);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications'
        },
        (payload: any) => {
          console.log('Notification updated:', payload.new);
          setNotifications(prev => 
            prev.map(notification => 
              notification.id === payload.new.id 
                ? payload.new as Notification 
                : notification
            )
          );
        }
      )
      .subscribe();

    return () => {
      directSupabase.removeChannel(channel);
    };
  }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const { data, error } = await directSupabase
        .from('notifications')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(showAll ? 50 : 10);

      if (error) {
        console.error('Error loading notifications:', error);
        return;
      }

      setNotifications(data || []);
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewNotification = (notification: Notification) => {
    setSelectedNotification(notification);
    setShowViewModal(true);
  };

  const markAsRead = async (notificationId: string) => {
    try {
      const { error } = await directSupabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      if (error) {
        console.error('Error marking notification as read:', error);
        return;
      }

      // Update local state
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, is_read: true }
            : notification
        )
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const { error } = await directSupabase
        .from('notifications')
        .update({ is_read: true })
        .eq('is_read', false);

      if (error) {
        console.error('Error marking all notifications as read:', error);
        return;
      }

      // Update local state
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, is_read: true }))
      );
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'form_submission': return 'primary';
      case 'lead_created': return 'success';
      default: return 'neutral';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'form_submission': return 'Form Submission';
      case 'lead_created': return 'New Lead';
      default: return type;
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;
  const displayNotifications = showAll ? notifications : notifications.slice(0, 10);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography level="h3" startDecorator={<IconWrapper icon={FaBell} />}>
            Notifications
          </Typography>
          {unreadCount > 0 && (
            <Badge
              badgeContent={unreadCount}
              color="danger"
            >
              <Box />
            </Badge>
          )}
        </Box>
        <Stack direction="row" spacing={1}>
          {unreadCount > 0 && (
            <Button
              size="sm"
              variant="outlined"
              onClick={markAllAsRead}
            >
              Mark All Read
            </Button>
          )}
          <Button
            size="sm"
            variant="outlined"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? 'Show Recent' : 'Show All'}
          </Button>
        </Stack>
      </Stack>

      <Card>
        {displayNotifications.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography level="body-lg" color="neutral">
              No notifications
            </Typography>
          </Box>
        ) : (
          <List>
            {displayNotifications.map((notification) => (
              <ListItem
                key={notification.id}
                sx={{
                  opacity: notification.is_read ? 0.7 : 1,
                  backgroundColor: notification.is_read ? 'background.level1' : 'background.surface'
                }}
              >
                <ListItemContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Chip
                        size="sm"
                        color={getTypeColor(notification.type)}
                        variant="soft"
                      >
                        {getTypeLabel(notification.type)}
                      </Chip>
                      {!notification.is_read && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: 'primary.500'
                          }}
                        />
                      )}
                    </Stack>
                    <Typography level="body-xs" color="neutral">
                      {format(new Date(notification.created_at), 'PPp')}
                    </Typography>
                  </Stack>
                  
                  <Typography level="body-md" mb={1}>
                    {notification.message}
                  </Typography>

                  {notification.metadata && (
                    <Box sx={{ mt: 1 }}>
                      <Typography level="body-xs" color="neutral">
                        Form ID: {notification.metadata.form_submission_id || '—'}
                      </Typography>
                      {notification.metadata.field_count && (
                        <Typography level="body-xs" color="neutral">
                          Fields: {notification.metadata.field_count}
                        </Typography>
                      )}
                    </Box>
                  )}
                </ListItemContent>

                <Stack direction="row" spacing={1}>
                  <IconButton
                    size="sm"
                    variant="plain"
                    color="neutral"
                    onClick={() => viewNotification(notification)}
                    title="View notification details"
                  >
                    <IconWrapper icon={FaEye} />
                  </IconButton>
                </Stack>
              </ListItem>
            ))}
          </List>
        )}
      </Card>

      {notifications.length > 10 && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button
            variant="outlined"
            onClick={() => setShowAll(!showAll)}
          >
            {showAll ? 'Show Recent 10' : `Show All ${notifications.length}`}
          </Button>
        </Box>
      )}

      {/* Notification Details Modal */}
      <Modal open={showViewModal} onClose={() => setShowViewModal(false)}>
        <ModalDialog>
          <DialogTitle>
            Notification Details
          </DialogTitle>
          <DialogContent>
            {selectedNotification && (
              <Stack spacing={2}>
                <Box>
                  <Typography level="body-sm" color="neutral">Type:</Typography>
                  <Chip
                    size="sm"
                    color={getTypeColor(selectedNotification.type)}
                    variant="soft"
                  >
                    {getTypeLabel(selectedNotification.type)}
                  </Chip>
                </Box>
                
                <Box>
                  <Typography level="body-sm" color="neutral">Message:</Typography>
                  <Typography>{selectedNotification.message}</Typography>
                </Box>
                
                <Box>
                  <Typography level="body-sm" color="neutral">Time:</Typography>
                  <Typography>{format(new Date(selectedNotification.created_at), 'PPp')}</Typography>
                </Box>

                {selectedNotification.metadata && (
                  <>
                    <Divider />
                    <Box>
                      <Typography level="body-sm" color="neutral">Form Submission ID:</Typography>
                      <Typography level="body-sm" fontFamily="monospace">
                        {selectedNotification.metadata.form_submission_id || '—'}
                      </Typography>
                    </Box>
                    
                    {selectedNotification.metadata.field_count && (
                      <Box>
                        <Typography level="body-sm" color="neutral">Fields Submitted:</Typography>
                        <Typography>{selectedNotification.metadata.field_count}</Typography>
                      </Box>
                    )}

                    {selectedNotification.metadata.form_data && (
                      <Box>
                        <Typography level="body-sm" color="neutral">Form Data:</Typography>
                        <Sheet
                          variant="outlined"
                          sx={{
                            mt: 1,
                            p: 2,
                            borderRadius: 'sm',
                            backgroundColor: 'background.level1',
                            maxHeight: 200,
                            overflow: 'auto'
                          }}
                        >
                          <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                            {JSON.stringify(selectedNotification.metadata.form_data, null, 2)}
                          </pre>
                        </Sheet>
                      </Box>
                    )}
                  </>
                )}

                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      markAsRead(selectedNotification.id);
                      setShowViewModal(false);
                    }}
                    disabled={selectedNotification.is_read}
                  >
                    Mark as Read
                  </Button>
                  <Button
                    variant="plain"
                    onClick={() => setShowViewModal(false)}
                  >
                    Close
                  </Button>
                </Stack>
              </Stack>
            )}
          </DialogContent>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default NotificationsDashboard; 